import { supabase } from '../lib/supabase';

/**
 * Tier limits for usage enforcement.
 * - templates: count of current templates (deleting frees quota)
 * - sessionsPerMonth, documentsPerMonth: monthly from usage_events (deletion does not free quota)
 */
export const TIER_LIMITS = {
  free: {
    name: 'Free',
    templates: 5,
    sessionsPerMonth: 5,
    documentsPerMonth: 5,
  },
  pro: {
    name: 'Pro',
    templates: 15,
    sessionsPerMonth: 60,
    documentsPerMonth: 30,
  },
  'pro-plus': {
    name: 'Pro Plus',
    templates: 100,
    sessionsPerMonth: 500,
    documentsPerMonth: 100,
  },
  enterprise: {
    name: 'Enterprise',
    templates: Infinity,
    sessionsPerMonth: Infinity,
    documentsPerMonth: Infinity,
  },
};

/**
 * Get start and end of current calendar month in ISO format.
 * @returns {{ start: string, end: string }}
 */
const getCurrentMonthRange = () => {
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);

  return {
    start: startOfMonth.toISOString(),
    end: endOfMonth.toISOString(),
  };
};

/** memberships.plan_name → tier key (same table as the edge `planNameToTier`). */
const PLAN_NAME_TO_TIER = {
  'Free Plan': 'free',
  'Free': 'free',
  'Pro Plan': 'pro',
  'Pro': 'pro',
  'Pro Plus Plan': 'pro-plus',
  'Pro Plus': 'pro-plus',
  'Enterprise Plan': 'enterprise',
  'Enterprise': 'enterprise',
};

/**
 * Map a memberships.plan_name to a tier key exactly like the server does.
 * @param {string | null | undefined} planName - Plan name.
 * @returns {string} Tier key: 'free' | 'pro' | 'pro-plus' | 'enterprise' (unknown/null → 'free').
 */
export const planNameToTier = (planName) => (planName && PLAN_NAME_TO_TIER[planName]) || 'free';

/**
 * Get user's tier for usage limits.
 * An account admin whose membership has no plan_name (internal/legacy accounts) keeps
 * unlimited usage limits ('enterprise'). AI model selection does NOT use this exception —
 * see {@link getAiTier}, which mirrors the server.
 * @param {object} currentMembership - User's current account membership.
 * @param {boolean} isAccountAdmin - Whether user is account admin.
 * @returns {string} Tier key: 'free' | 'pro' | 'pro-plus' | 'enterprise'
 */
export const getUserTier = (currentMembership, isAccountAdmin) => {
  if (currentMembership?.plan_name) {
    return planNameToTier(currentMembership.plan_name);
  }

  if (isAccountAdmin) {
    return 'enterprise';
  }

  return 'free';
};

/**
 * Tier that decides which AI model the server uses (mirrors gemini-proxy's
 * `fetchAccountTierForUser`: plan_name only, missing plan → 'free').
 * @param {object} currentMembership - User's current account membership.
 * @returns {string} Tier key.
 */
export const getAiTier = (currentMembership) => planNameToTier(currentMembership?.plan_name);

/**
 * Get tier limits for a user.
 * @param {string} userTier - Tier key.
 * @returns {object} Tier limits object.
 */
export const getTierLimits = (userTier) => {
  return TIER_LIMITS[userTier] || TIER_LIMITS.free;
};

/**
 * Get limit feature strings for display in pricing/plan UI.
 * @param {string} tier - Tier key.
 * @param {object} [opts] - Options.
 * @param {boolean} [opts.titleCase] - Use title case (e.g. "5 Templates") vs sentence case.
 * @returns {string[]} Array of limit feature strings (templates, sessions, documents).
 */
export const getPlanFeaturesForDisplay = (tier, opts = {}) => {
  const limits = getTierLimits(tier);
  const features = [];
  const cap = (s) => (opts.titleCase ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  if (limits.templates === Infinity) {
    features.push(cap('unlimited templates'));
  } else {
    features.push(`${limits.templates} ${cap('template')}${limits.templates !== 1 ? 's' : ''}`);
  }

  if (limits.sessionsPerMonth === Infinity) {
    features.push(cap('unlimited sessions'));
  } else {
    features.push(`${limits.sessionsPerMonth} ${cap('sessions')} per month`);
  }

  if (limits.documentsPerMonth === Infinity) {
    features.push(cap('unlimited documents'));
  } else {
    features.push(`${limits.documentsPerMonth} ${cap('documents')} per month`);
  }

  return features;
};

/**
 * Message for a usage count that could not be read (the check is retried on the next try).
 * UI copy: no em dashes.
 */
export const USAGE_UNKNOWN_MESSAGE = "We couldn't check your plan usage right now. Please try again in a moment.";

/** Extra attempts for a failed count before it is reported as unknown. */
const COUNT_RETRIES = 1;

/**
 * Run a count, retrying a failure once.
 * @param {() => Promise<number>} fn - Count query (throws on error).
 * @returns {Promise<number>} Count.
 */
const withRetry = async (fn) => {
  let lastError = null;
  for (let attempt = 0; attempt <= COUNT_RETRIES; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
};

/**
 * Count current templates for user/account. Deleting a template frees quota.
 * @param {string} userId - User ID.
 * @param {string} accountId - Account ID.
 * @returns {Promise<number>} Count.
 * @throws {Error} When the count cannot be read (never reported as 0).
 */
export const getTemplateUsage = async (userId, accountId) => {
  try {
    const { data, error } = await supabase
      .from('templates')
      .select('id')
      .eq('user_id', userId)
      .eq('account_id', accountId);

    if (error) throw error;
    return data?.length ?? 0;
  } catch (error) {
    console.error('Error fetching template usage:', error);
    throw error;
  }
};

/**
 * Count usage_events of one resource type this month (append-only, deletion-safe).
 * @param {string} userId - User ID.
 * @param {string} accountId - Account ID.
 * @param {'session'|'document'} resourceType - usage_events.resource_type.
 * @returns {Promise<number>} Count.
 * @throws {Error} When the count cannot be read.
 */
const countMonthlyEvents = async (userId, accountId, resourceType) => {
  const { start, end } = getCurrentMonthRange();

  const { count, error } = await supabase
    .from('usage_events')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('account_id', accountId)
    .eq('resource_type', resourceType)
    .gte('created_at', start)
    .lte('created_at', end);

  if (error) throw error;
  if (typeof count !== 'number') throw new Error('Usage count unavailable');
  return count;
};

/**
 * Count session creations this month from usage_events (append-only, deletion-safe).
 * @param {string} userId - User ID.
 * @param {string} accountId - Account ID.
 * @returns {Promise<number>} Count.
 * @throws {Error} When the count cannot be read (never reported as 0).
 */
export const getSessionUsage = async (userId, accountId) => {
  try {
    return await countMonthlyEvents(userId, accountId, 'session');
  } catch (error) {
    console.error('Error fetching session usage:', error);
    throw error;
  }
};

/**
 * Count document (generated_documents) creations this month from usage_events (append-only, deletion-safe).
 * @param {string} userId - User ID.
 * @param {string} accountId - Account ID.
 * @returns {Promise<number>} Count.
 * @throws {Error} When the count cannot be read (never reported as 0).
 */
export const getDocumentUsage = async (userId, accountId) => {
  try {
    return await countMonthlyEvents(userId, accountId, 'document');
  } catch (error) {
    console.error('Error fetching document usage:', error);
    throw error;
  }
};

/**
 * One usage entry: a known count, or `unknown` when the count could not be read.
 * @param {{value?: number, failed?: boolean}} result - Settled count.
 * @param {number} limit - Tier limit.
 * @returns {{current: number|null, limit: number, percentage: number, unknown?: boolean}} Entry.
 */
const usageEntry = (result, limit) => {
  if (result.failed) return { current: null, limit, percentage: 0, unknown: true };
  const current = result.value;
  return {
    current,
    limit,
    percentage: limit === Infinity ? 0 : Math.round((current / limit) * 100),
  };
};

/**
 * Run a count with one retry; a failure is reported, never turned into 0.
 * @param {() => Promise<number>} fn - Count.
 * @returns {Promise<{value?: number, failed?: boolean}>} Settled result.
 */
const settleCount = async (fn) => {
  try {
    return { value: await withRetry(fn) };
  } catch {
    return { failed: true };
  }
};

/**
 * Get comprehensive usage data for a user.
 *
 * A count that cannot be read (after one retry) is reported as `unknown` with
 * `limitUnknown: true` and `error: USAGE_UNKNOWN_MESSAGE`, never as 0. {@link hasReachedLimit}
 * then blocks creation on the Free tier only; paid tiers are never blocked by a failed count.
 * @param {string} userId - User ID.
 * @param {string} accountId - Account ID.
 * @param {object} currentMembership - User's current membership.
 * @param {boolean} isAccountAdmin - Whether user is account admin.
 * @returns {Promise<object>} `{tier, tierName, usage: {templates, sessions, documents}, limitUnknown, unknownTypes, error}`.
 */
export const getUserUsage = async (userId, accountId, currentMembership, isAccountAdmin) => {
  const userTier = getUserTier(currentMembership, isAccountAdmin);
  const limits = getTierLimits(userTier);

  let templates = { failed: true };
  let sessions = { failed: true };
  let documents = { failed: true };
  try {
    [templates, sessions, documents] = await Promise.all([
      settleCount(() => getTemplateUsage(userId, accountId)),
      settleCount(() => getSessionUsage(userId, accountId)),
      settleCount(() => getDocumentUsage(userId, accountId)),
    ]);
  } catch (error) {
    console.error('Error fetching user usage:', error);
  }

  const usage = {
    templates: usageEntry(templates, limits.templates),
    sessions: usageEntry(sessions, limits.sessionsPerMonth),
    documents: usageEntry(documents, limits.documentsPerMonth),
  };
  const unknownTypes = Object.keys(usage).filter((type) => usage[type].unknown);
  const limitUnknown = unknownTypes.length > 0;

  return {
    tier: userTier,
    tierName: limits.name,
    usage,
    limitUnknown,
    unknownTypes,
    error: limitUnknown ? USAGE_UNKNOWN_MESSAGE : null,
  };
};

/**
 * Whether a usage count could not be read (show {@link USAGE_UNKNOWN_MESSAGE} and offer a
 * retry instead of an upgrade prompt).
 * @param {object|null|undefined} usage - Usage data from getUserUsage.
 * @param {string} type - 'templates' | 'sessions' | 'documents'
 * @returns {boolean} True when the count is unknown.
 */
export const isLimitUnknown = (usage, type) => Boolean(usage?.usage?.[type]?.unknown);

/**
 * Check if user has reached a specific usage limit.
 *
 * Fails closed only where it matters: when the count is unknown, the Free tier is blocked
 * (the caller should show {@link USAGE_UNKNOWN_MESSAGE}, see {@link isLimitUnknown}); Pro,
 * Pro Plus and Enterprise are never blocked because a count query failed.
 * @param {object} usage - Usage data from getUserUsage.
 * @param {string} type - 'templates' | 'sessions' | 'documents'
 * @returns {boolean}
 */
export const hasReachedLimit = (usage, type) => {
  const typeUsage = usage?.usage?.[type];
  if (!typeUsage) return false;
  if (typeUsage.limit === Infinity) return false;

  if (typeUsage.unknown || typeof typeUsage.current !== 'number') {
    return (usage.tier || 'free') === 'free';
  }

  return typeUsage.current >= typeUsage.limit;
};

/**
 * Check if user is approaching a limit (>= 80%).
 * @param {object} usage - Usage data from getUserUsage.
 * @param {string} type - 'templates' | 'sessions' | 'documents'
 * @returns {boolean}
 */
export const isApproachingLimit = (usage, type) => {
  const typeUsage = usage?.usage?.[type];
  if (!typeUsage || typeUsage.limit === Infinity || typeUsage.unknown) return false;

  return typeUsage.percentage >= 80;
};
